import {
  Box,
  Container,
  Divider,
  HStack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  Stack,
  Heading,
  Button,
} from '@chakra-ui/react';
import { OAuthButtonGroup } from './OAuthButtonGroup';
import RegisterForm, { RegisterFormData } from '../Form/RegisterForm/RegisterForm';
import { Logo } from 'components';
import { FeatureToggleService } from 'services';
import { FeatureToggleKeys } from 'constants/toggles';

export interface RegisterCardProps {
  onRegisterClick?: (email: string, username: string, password: string) => void;
  registerLoading?: boolean;
  onSwitchToLogin?: () => void;
}

export default function RegisterCard({
  onRegisterClick,
  registerLoading = false,
  onSwitchToLogin,
}: RegisterCardProps) {
  const showOAuthButtons = FeatureToggleService.isToggleEnabled(
    FeatureToggleKeys.AUTH_OAUTH_BUTTONS
  );

  function renderOAuthButtons() {
    if (showOAuthButtons) {
      return <OAuthButtonGroup />;
    }
    return null;
  }

  function renderOAuthSeperator() {
    if (showOAuthButtons) {
      return (
        <HStack>
          <Divider />
          <Text fontSize="sm" whiteSpace="nowrap" color="muted">
            or sign up with
          </Text>
          <Divider />
        </HStack>
      );
    }
    return null;
  }

  function renderHeader() {
    return (
      <Stack spacing="8" align="center">
        <Logo height="120px" width="120px" />
        <Stack spacing="6" w="full">
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading fontSize="xl">Create an account</Heading>
            {onSwitchToLogin ? (
              <HStack spacing="1" justify="center" fontSize="sm">
                <Text color="muted">Already have an account?</Text>
                <Button
                  variant="link"
                  colorScheme="blue"
                  onClick={onSwitchToLogin}
                >
                  Log in
                </Button>
              </HStack>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  const onFormSubmit = (data: RegisterFormData) => {
    if (onRegisterClick) {
      onRegisterClick(data.email, data.username, data.password);
    }
  };

  return (
    <Box
      minW={{base: 'auto', 'md': 'md'}}
      py={{ base: '0', sm: '8' }}
      px={{ base: '4', sm: '10' }}
      bg={useBreakpointValue({ base: 'transparent', sm: 'bg-surface' })}
      boxShadow={{ base: 'none', sm: useColorModeValue('md', 'md-dark') }}
      borderRadius={{ base: 'none', sm: 'xl' }}
    >
      <Stack spacing="8">
        {renderHeader()}
        <Stack spacing="6">
          <RegisterForm onSubmit={onFormSubmit} submitLoading={registerLoading} />
          <Stack spacing="6">
            {renderOAuthSeperator()}
            {renderOAuthButtons()}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
